import React, { useContext, useMemo } from 'react';
import { Stack, Typography, Icon } from '@mui/material';
import { IconButton, TextField, Tooltip } from '@engyalo/design-system';
import { useTranslation } from 'react-i18next';
import useMobile from 'hooks/useMobile';
import useInputMessage from './useInputMessage';
import { ModalErrorPermission } from './modalErrorPermission';
import CannedMessagesContext from '../../../../../contexts/cannedMessages/context';
import SelectAgentContext from '../../../../../contexts/selectAgentContext/context';
import { AttachFile } from '../../../AttachFile';
import { SendMessage } from '../../hooks/useChat';
import { ActionButton, ActionsStack, SendButton } from '../style';
import { QuickMessageTagIcon } from './style';

export interface IInputMessage {
  discussionId: string;
  isDiscussion: boolean;
  showQuickMessagesIcon: boolean;
  isWsConnected?: boolean;
  sendMessage?: SendMessage;
  isChatLoading?: boolean;
}

const InputMessage: React.FC<IInputMessage> = (props) => {
  const { discussionId, isDiscussion, showQuickMessagesIcon, isWsConnected, isChatLoading, sendMessage } = props;
  const { t } = useTranslation();
  const { isMobileView } = useMobile();

  const {
    currentMessage,
    setCurrentMessage,
    handleSendMessage,
    startRecordingAudio,
    sendAudio,
    cancelAudio,
    timerRecoder,
    isRecording,
    openErrorPermission,
    setErrorPermission,
    translation,
    setAlignment,
    changeSendButtonAlignment,
    placeholderInput,
    disabledInputChat,
    focusDiv,
    discussionOpened,
    currentMessageDiscussion,
    setCurrentMessageDiscussion,
    userIsInDiscussion,
    disableInput,
    setDisableInput,
    selectedCannedMessageTags,
    handleForceAddTagsFromCannedMessage,
    clearSelectedCannedMessage,
  } = useInputMessage(discussionId, isDiscussion, sendMessage);

  const isInputDisabled = isChatLoading || disableInput;

  const { handleQuickMessages, filterCannedMessages, handleMessageSelection, showQuickMessages } =
    useContext(CannedMessagesContext);
  const { handleSelectAgent, filterAgents, showSelectAgent } = useContext(SelectAgentContext);
  useMemo(() => {
    const checkInputIsEmpty = isDiscussion ? !currentMessageDiscussion : !currentMessage;
    setDisableInput(checkInputIsEmpty);
  }, [isDiscussion, currentMessageDiscussion, currentMessage]);

  return (
    <>
      <Stack
        direction="row-reverse"
        width="100%"
        alignItems="flex-end"
        data-testid="inputMessage"
        sx={{ margin: '8px 0px' }}
      >
        <TextField
          inputRef={focusDiv}
          id="messageInput"
          minRows={1}
          maxRows={5}
          multiline
          disabled={discussionOpened && userIsInDiscussion ? false : disabledInputChat}
          placeholder={translation(placeholderInput)}
          value={isDiscussion ? currentMessageDiscussion : currentMessage}
          onChange={(event) => {
            const {
              target: { value },
            } = event;
            if (!currentMessage && value === '!' && !isDiscussion) {
              handleQuickMessages();
            }
            if (isDiscussion) {
              setCurrentMessageDiscussion(value);
            } else {
              setCurrentMessage(value);
              clearSelectedCannedMessage();
            }

            if (isDiscussion && showSelectAgent && value) {
              filterAgents(value);
            }
            if (showQuickMessages) {
              filterCannedMessages(value);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === '@') {
              handleSelectAgent?.(); // TODO - this is not working
            }
            if (event.key === 'Tab') {
              handleMessageSelection(true);
            }
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
            }
          }}
          onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              const { value } = event.target as HTMLInputElement;

              if (isWsConnected && !isInputDisabled) {
                handleSendMessage(value);
              }
            }
            setAlignment(changeSendButtonAlignment());
          }}
          sx={{
            order: 1,
            flex: 1,
            display: isDiscussion && isRecording ? 'none' : 'block',
            '& .MuiInputBase-input': {
              overflow: 'auto',
              textOverflow: 'ellipsis',
            },
            '& .MuiInputBase-root': {
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
          inputProps={{ 'data-testid': 'inputMessageText' }}
          InputProps={{
            endAdornment: selectedCannedMessageTags.length > 0 && (
              <Tooltip
                title={`${t('quickMessagesTags', {
                  numberOfTags: selectedCannedMessageTags.length,
                })}`}
                placement="top"
              >
                <QuickMessageTagIcon className="fa-regular fa-tags" />
              </Tooltip>
            ),
          }}
        />

        {!currentMessage && !currentMessageDiscussion ? (
          <ActionsStack alignItems="flex-end">
            {isRecording ? (
              <>
                <IconButton onClick={() => cancelAudio()} variant="text" color="error">
                  <i className="fa-regular fa-circle-xmark fa-sm" />
                </IconButton>
                <Icon
                  className="fa-solid fa-circle fa-sm"
                  sx={{
                    color: 'neutral.var70',
                    margin: '0px 10px',
                  }}
                />

                <Typography sx={{ width: '50px' }}>{timerRecoder}</Typography>
                <IconButton data-testid="sendButton" onClick={() => sendAudio()} variant="text" color="success">
                  <i className="fa-regular fa-circle-check fa-sm" />
                </IconButton>
              </>
            ) : (
              <>
                {isMobileView && (
                  <>
                    <AttachFile disabled={userIsInDiscussion ? false : disabledInputChat} sendMessage={sendMessage} />

                    {showQuickMessagesIcon && (
                      <Tooltip title={`${t('quickMessages')}`} placement="top">
                        <ActionButton
                          disabled={disabledInputChat}
                          color={showQuickMessages ? 'primary' : 'neutral'}
                          onClick={handleQuickMessages}
                          variant="text"
                        >
                          <i className="fa-regular fa-square-bolt" />
                        </ActionButton>
                      </Tooltip>
                    )}
                  </>
                )}

                <Tooltip title={`${t('recordVoiceMessage')}`} placement="top">
                  <ActionButton
                    disabled={discussionOpened ? false : disabledInputChat}
                    onClick={() => startRecordingAudio()}
                    variant="text"
                    color="neutral2"
                  >
                    <i className="fa-regular fa-microphone" />
                  </ActionButton>
                </Tooltip>
              </>
            )}
          </ActionsStack>
        ) : (
          <ActionsStack>
            {isMobileView && (
              <AttachFile disabled={userIsInDiscussion ? false : disabledInputChat} sendMessage={sendMessage} />
            )}
            <SendButton
              data-testid="sendMessageBtn"
              color="primary"
              disabled={disabledInputChat || !isWsConnected || isInputDisabled}
              onClick={() => {
                handleSendMessage(isDiscussion ? currentMessageDiscussion : currentMessage);
                handleForceAddTagsFromCannedMessage();
              }}
            >
              <i className="fa-solid fa-arrow-up" />
            </SendButton>
          </ActionsStack>
        )}
      </Stack>

      <ModalErrorPermission openErrorPermission={openErrorPermission} setErrorPermission={setErrorPermission} />
    </>
  );
};

export default InputMessage;
